import { useAppContext } from "@context/app";
import Address from "@modules/cart/address";
import Cart from "@modules/cart/cart";
import Gift from "@modules/cart/gift";
import {
  CartItemModel,
  CartStep,
  IOrderCheckCartOutput,
  IOrderGiftCartOutput,
  IOrderPersonalization,
  ISkuModel,
} from "@types";
import { getNotifyConfig } from "@utils/helpers";
import { segmentEvent } from "@utils/segment";
import Loading from "assets/lottie/loading";
import { orderApi } from "gate";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { differenceBy } from "lodash";
import RemoveItemToWishlistModal from "@modules/cart/RemoveItemToWishlistModal";
import { wishlistApi } from "gate";


interface CartContainerProps { }

export type CartProblemType = "less" | "unavailable" | "less-registry-item" | "non-registry-item" | "registry-item-purchased" | "balloon-less"
export type WrappingProblemType = "unavailable-wrapping" | "unwrappable" | "balloon-unavailable"

const CartContainer: React.FC<CartContainerProps> = () => {
  const [step, setStep] = useState<CartStep>("cart");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
  const [personalizations, setPersonalizations] = useState<IOrderPersonalization[]>([]);
  const [personalizationSource, setPersonalizationSource] = useState<string>("");
  const [hasShownRecommendations, setHasShownRecommendations] = useState<boolean>(false);

  const [relatedSkus, setRelatedSkus] = useState<ISkuModel[]>([]);
  const [relatedSkusForModal, setRelatedSkusForModal] = useState<ISkuModel[]>([]);
  const [cartProblems, setCartProblems] = useState<
    { id: string | number; type: CartProblemType ; count?: number }[]
  >([]);
  const [wrappingProblems, setWrappingProblems] = useState<
    { id: string | number; type: WrappingProblemType }[]
  >([]);
  const [showWishlistModal, setShowWishlistModal] = useState<boolean>(false);
  const [isAddingToWishlist, setIsAddingToWishlist] = useState<boolean>(false);
  const [removedItems, setRemovedItems] = useState<CartItemModel[]>([]);
  const router = useRouter();
  const { push } = router;
  const { setUserCart, cart, user, country, area, eventProperties, wishlistProductIds, setWishlistProductIds } = useAppContext();
  const [hasCustomizationFeatureAnnouncement, setHasCustomizationFeatureAnnouncement] = useState<boolean>(false);

  useEffect(() => {
    if (cart.length && isFirstTime) {
      checkCart({ current: "cart" })
      setIsFirstTime(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, isFirstTime])

  const changeStep = (value: CartStep | "checkout") => {
    if (value === "checkout") {
      push("/checkout")
      return
    }
    setStep(value);
  };


  useEffect(() => {
    if (!cart.length) {
      changeStep("cart")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart])




  const readyForRelatedModal = () => {
    const res = differenceBy([...relatedSkus], cart, "id")
    setRelatedSkusForModal([...res])
    return res.length ? true : false
  }


  const preparingCart = () => {
    const checkCartValue: IOrderCheckCartOutput = { items: [] };
    const items = [...cart];
    for (const sku of items) {
      const digital_gift_ids: number[] | undefined = sku.digital_gift_ids;
      const registry_contribution_ids: number[] | undefined = sku.registry_contribution_ids;
      let map = new Array(sku.qty);
      const gift: IOrderGiftCartOutput[] = [];
      for (const [index] of map.entries()) {
        if (sku.gifts[index]) {
          const target = sku.gifts[index];
          const balloons = Array.isArray(target.balloons)
            ? target.balloons
                .filter((b) => (Number(b?.count) || 0) > 0)
                .map((b) => ({ id: String(b.id), count: Number(b.count) }))
            : [];
          const giftItem: IOrderGiftCartOutput = {
            wrapping: target.wrapping?.id || null,
            card: target.card?.id
              ? {
                  id: target.card.id,
                  from: target.card.from || undefined,
                  to: target.card.to || undefined,
                  message: target.card.message || undefined,
                }
              : null,
            // Keep payload lean: only send balloon id + count.
            customtext: sku.customText ? sku.customText[index]?.text : ""
          };
          if (balloons.length) {
            giftItem.balloons = balloons;
          }
          gift.push(giftItem);
        } else {
          gift.push({
            wrapping: null,
            card: null,
            customtext: sku.customText ? sku.customText[index]?.text : ""
          });
        }
      }
      checkCartValue.items.push({
        id: parseInt(sku.id as any),
        count: sku.qty,
        digital_gift_ids,
        gift,
        registry_contribution_ids
      });
    }

    return checkCartValue;
  };

  const checkCart = async (value: {
    next?: CartStep;
    current: CartStep;
    goCheckout?: boolean;
    callBack?: (data: CartItemModel[]) => void;
    returnRelatedItems?: (data: ISkuModel[], nextStep: CartStep | "checkout" | null) => void
  }) => {
    setIsLoading(true);
    let newCart = [...cart];
    const preparedCart = preparingCart();
    if (preparedCart.items.length) {
      try {
        const res = await orderApi.checkCart(preparedCart).fetch();
        if(res.meta.msg && value.current !== "address"){
          toast.error(res.meta.msg, getNotifyConfig());
        }
        const newPersonalizations = res.data.personalizations || [];
        setPersonalizations(newPersonalizations);
        setPersonalizationSource(res.data.personalization_source);
        setHasCustomizationFeatureAnnouncement(res.data.has_feature_announcement || false);
        // Check for "Recommendation Items Shown" event
        if (value.current === "cart" && !hasShownRecommendations && newPersonalizations.length > 0) {
          const recommendedPersonalization = newPersonalizations.find(p => p.type === "Recommended");
          if (recommendedPersonalization) {
            if (recommendedPersonalization.products.length > 0) {
              segmentEvent("Recommendation Items Shown", {
                area_id: area?.id,
                area_slug: area?.slug,
                country_id: country?.id,
                country_slug: country?.slug,
                environment: process.env.NEXT_PUBLIC_SENTRY_ENV,
                ...eventProperties,
                language: router.locale?.includes("en") ? "English" : "Arabic",
                user_id: user?.data?.id || -1,
                recommendation_count: recommendedPersonalization.products.length,
                recommendations_source: res.data.personalization_source,
                recommendation_sku_ids: recommendedPersonalization.products.map(p => p.default_sku_id),
              });
              setHasShownRecommendations(true);
            }
          }
        }
        let nextStep: CartStep | "checkout" | null = null;
        if (res.data.skip_address && (value.current === "address" || value.next === "address" || value.returnRelatedItems)) {
          nextStep = "gift";
          if (value.current === "gift" && value.next === "address") {
            value.next = "cart";
            nextStep = "cart";
          }
        }

        if (value.next === "gift" || value.current === "gift" || nextStep === "gift") {
          if (res.data.cart.every(ci => ci.sku.no_customizations)) {
            nextStep = "checkout";
          }
        }

        if (res.data.suggestionSku?.length) {
          setRelatedSkus(res.data.suggestionSku)
          value.returnRelatedItems?.(res.data.suggestionSku, nextStep || "address")
        } else {
          setRelatedSkus([])
          if (value.returnRelatedItems) {
            value.returnRelatedItems([], nextStep || "address")
            value.next = "address"
          }
        }
        if (nextStep && (!value.returnRelatedItems || !res.data.suggestionSku?.length)) {
          switch (nextStep) {
            case "gift":
              value.next = "gift";
              break;
            case "checkout":
              value.goCheckout = true;
              break;
          }
        }

        newCart = newCart.map((item) => {
          const target = res.data.cart.find((p) => p.id === item.id);
          if (target) {
            item.product_source = target.sku.product?.product_source || "regular";
            item.no_customizations = target.sku.no_customizations;
            item.has_balloons = target.sku.has_balloons;
            item.assembly_price = target.sku.assembly_price?.price;
          }
          return item
        })
        if (res.data.problems.length) {
          const itemsToRemove: CartItemModel[] = [];
          const nextCartProblems: typeof cartProblems = [];
          const nextWrappingProblems: typeof wrappingProblems = [];

          for (const problem of res.data.problems) {
            switch (problem.type) {
              case "less": {
                const target = newCart.find((p) => p.id === problem.id);
                if (target) {
                  target.buy_limit = problem.available ?? 0;
                  target.qty = problem.available ?? 0;
                  nextCartProblems.push({ id: target.id, type: "less", count: problem.available });
                }
                break;
              }
              case "less-registry-item": {
                const target = newCart.find((p) => p.id === problem.id);
                if (target) {
                  target.buy_limit = problem.available ?? 0;
                  target.qty = problem.available ?? 0;
                  nextCartProblems.push({ id: target.id, type: "less-registry-item", count: problem.available });
                }
                break;
              }
              case "non-registry-item": {
                const target = newCart.find((p) => p.id === problem.id);
                if (target) {
                  itemsToRemove.push({ ...target });
                  target.buy_limit = problem.available ?? 0;
                  target.qty = problem.available ?? 0;
                  nextCartProblems.push({ id: target.id, type: "non-registry-item", count: problem.available });
                }
                newCart = newCart.filter((p) => p.id !== problem.id);
                break;
              }
              case "registry-item-purchased": {
                const target = newCart.find((p) => p.id === problem.id);
                if (target) {
                  target.buy_limit = problem.available ?? 0;
                  target.qty = problem.available ?? 0;
                  nextCartProblems.push({ id: target.id, type: "registry-item-purchased", count: problem.available });
                }
                newCart = newCart.filter((p) => p.id !== problem.id);
                break;
              }
              case "unavailable": {
                const unavailableItem = newCart.find((p) => p.id === problem.id);
                if (unavailableItem) {
                  itemsToRemove.push({ ...unavailableItem });
                }
                newCart = newCart.filter((p) => p.id !== problem.id);
                nextCartProblems.push({ id: problem.id, type: "unavailable" });
                break;
              }
              case "unavailable-wrapping":
              case "balloon-unavailable": {
                const target = newCart.find((p) => p.id === problem.id);
                if (target) {
                  target.gifts.map((g) => {
                    if (problem.type === "unavailable-wrapping" && g.wrapping?.id === problem.wrapping) {
                      g.wrapping = null;
                    }
                    if (problem.type === "balloon-unavailable") {
                      const unavailableBalloonId = problem.balloon;
                      if (unavailableBalloonId === undefined || unavailableBalloonId === null) {
                        (g as any).balloons = [];
                      } else {
                        (g as any).balloons = Array.isArray((g as any).balloons)
                          ? (g as any).balloons.filter(
                            (b: any) => Number(b?.id) !== Number(unavailableBalloonId)
                          )
                          : [];
                      }
                    }
                  });
                  nextWrappingProblems.push({ id: problem.id, type: problem.type });
                }
                break;
              }
              case "balloon-less": {
                const localItem = newCart.find((p) => p.id === problem.id);
                if (localItem) {
                    const backendItem = res.data.cart.find(
                        (ci) => Number(ci.id) === Number(problem.id)
                    );
                    if (backendItem) {
                        backendItem.gift.forEach((bg, idx) => {
                            if (localItem.gifts[idx]) {
                                localItem.gifts[idx].balloons = bg.balloons || [];
                            }
                        });
                    }
                    nextCartProblems.push({ id: problem.id, type: "balloon-less" });
                }
                break;
              }
              case "unwrappable": {
                const target = newCart.find((p) => p.id === problem.id);
                if (target) {
                  target.gifts.map((g) => {
                      g.wrapping = null;
                  });
                  nextWrappingProblems.push({ id: problem.id, type: "unwrappable" });
                }
                break;
              }
              default:
                break;
            }
          }

          setCartProblems(nextCartProblems);
          setWrappingProblems(nextWrappingProblems);

          if (value.current === "address" && value.callBack) {
            value.callBack(newCart);
          } else {
            setUserCart([...newCart]);
            setStep("cart");
            if (itemsToRemove.length > 0) {
              setRemovedItems(itemsToRemove);
              setShowWishlistModal(true);
            }
          }
        } else {
          const handleNextStepFromCart = !!(value.current === "cart" && value.returnRelatedItems)
          if (value.goCheckout && !handleNextStepFromCart) {
            push("/checkout");
          } else if (value.next && !handleNextStepFromCart) {
            setStep(value.next);
          }
        }
        if (value.current === "cart") {
          setUserCart([...newCart]);
        }
      } catch (error: any) {
        if (error?.error?.code === 406) {
          toast.error(
            error?.error?.message || "Something Went Wrong",
            getNotifyConfig()
          );
        }
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    readyForRelatedModal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedSkus])

  const handleAddToWishlist = async () => {
    if (removedItems.length === 0) {
      setShowWishlistModal(false);
      return;
    }

    // First, add all items to wishlist as normal
    try {
      setIsAddingToWishlist(true);
      const productIds = removedItems.map(item => item.productId);
      const response = await wishlistApi.addToDefaultWishlist(productIds).fetch();

      if (response.meta.code === 200) {
        // Update wishlist product IDs
        const newProductIds = [...wishlistProductIds, ...productIds];
        const uniqueProductIds = [...new Set(newProductIds)]; // Remove duplicates
        setWishlistProductIds(uniqueProductIds);
        toast.success("Items added to wishlist");

        // After successfully adding to wishlist, check conditions to add to wishlist personalization tab
        const wishlistPersonalization = personalizations?.find(p => p.type === "Wishlist");

        if (wishlistPersonalization) {
          const removedItemsToAdd: any[] = []
          for (const removedItem of removedItems) {
            // 1. User added the removed cart item to wishlist (we're here, so yes)

            // 2. Check if the removed item is in stock
            if (removedItem.buy_limit && removedItem.buy_limit > 0) {



              // 3. Wishlist personalization exists (checked above)

              // 4. Check if there is an item in the wishlist personalization products that matches the condition
              const existingItem = wishlistPersonalization.products.find(
                p => removedItem.id === p.default_sku_id
              );

              // 5. If no item was found, add the removed item to the wishlist personalization
              if (!existingItem) {
                // Convert CartItemModel to IProductDetailModel format to add to personalization
                const productToAdd: any = {
                  id: removedItem.productId,
                  name: removedItem.productName,
                  slug: removedItem.productSlug,
                  default_sku_id: removedItem.id,
                  skus: [{
                    id: removedItem.id,
                    name: removedItem.skuName,
                    slug: removedItem.skuSlug,
                    buy_limit: removedItem.buy_limit,
                    price: {
                      price: removedItem.price,
                      raw_price: removedItem.raw_price,
                      old_price: removedItem.oldPrice,
                    },
                    medias: [{
                      url: removedItem.image,
                    }],
                  }],
                  brand: {
                    id: removedItem.brand_id,
                    slug: removedItem.brand_slug,
                  },
                  category: {
                    id: removedItem.category_id,
                    slug: removedItem.category_slug,
                  },
                  subcategory: removedItem.subcategory_id ? {
                    id: removedItem.subcategory_id,
                    slug: removedItem.subcategory_slug,
                  } : undefined,
                };
                removedItemsToAdd.push(productToAdd);
              }

            }
          }
          setPersonalizations((prev) => {
            const wishlistIndex = prev.findIndex(p => p.type === "Wishlist");
            if (wishlistIndex !== -1) {
              const newPersonalizations = [...prev];
              newPersonalizations[wishlistIndex] = {
                ...wishlistPersonalization,
                products: [...wishlistPersonalization.products, ...removedItemsToAdd],
              };
              return newPersonalizations;
            }
            return prev;
          });
        }
      }
    } catch (error) {
      console.error("Error adding items to wishlist:", error);
    } finally {
      setIsAddingToWishlist(false);
      setShowWishlistModal(false);
      setRemovedItems([]);
    }
  };

  const handleCancelWishlist = () => {
    setShowWishlistModal(false);
    setRemovedItems([]);
  };

  return (
    <div className="w-full max-w-[1200px] mx-auto px-2">
      {(isLoading) && <Loading isCenter />}

      {step === "cart" && (
        <Cart
          changeStep={changeStep}
          action={checkCart}
          cartProblems={cartProblems}
          removeProblems={() => setCartProblems([])}
          extraItems={relatedSkusForModal}
          onReady={readyForRelatedModal}
          hasRelated={readyForRelatedModal}
          personalizations={personalizations}
          personalizationSource={personalizationSource}
        />
      )}
      {step === "address" && (
        <Address
          changeStep={changeStep}
          setIsLoading={setIsLoading}
          action={checkCart}
        />
      )}
      {step === "gift" && (
        <Gift
          hasFeatureAnnouncement={hasCustomizationFeatureAnnouncement}
          changeStep={changeStep}
          action={checkCart}
          wrappingProblems={wrappingProblems}
          removeProblems={() => setWrappingProblems([])}
        />
      )}

      <RemoveItemToWishlistModal
        isOpen={showWishlistModal}
        onClose={handleCancelWishlist}
        onAddToWishlist={handleAddToWishlist}
        onCancel={handleCancelWishlist}
        isLoading={isAddingToWishlist}
      />
    </div>
  );
};

export default CartContainer;
